import React, { useState, useEffect, useRef } from 'react'
import { ButtonToolbar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import * as fromQuestionActions from 'store/actions/questions.action'
import { IMainState } from 'store/states'
import Button from 'design-system/button'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import { IQuestionData } from 'models/question.model'
import {
  prepareLikertQuestionData,
  prepareOpenEndedData,
} from 'helpers/converters.helper'
import {
  ILikerOptionModel,
  IQuestionLikertModel,
} from 'models/questionLikert.model'
import { IOpenEndedModel } from 'models/questionOpenEnded.model'
import { BlockTypes } from 'enum/block-type.enum'
import SurveyQuestionsEditor from './components/survey-questions-editor/SurveyQuestionsEditor'
import SurveyQuestionCard from './components/survey-question-card/SurveyQuestionCard'
import { Confirmation } from '../Confirmation'
import styles from './SurveyQuestions.module.scss'

const questionDefaultValues = {
  name: '',
  mandatory: false,
  type: { value: '', label: '' },
}

const likertAnswerDefaultValues: ILikerOptionModel[] = [
  { label: '' },
  { label: '' },
  { label: '' },
  { label: '' },
  { label: '' },
]
interface ISurveyQuestions {
  onCancel: () => void
  blockId: number | undefined
  instanceId: number | undefined
}

const SurveyQuestions: React.FC<ISurveyQuestions> = ({
  blockId,
  instanceId,
  onCancel,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [questions, setQuestions] = useState<IQuestionData[]>([])
  const [onEdit, setOnEdit] = useState(0)
  const confirmed = useRef(false)
  const promiseResolve = useRef<(() => void) | null>(null)
  const [questionData, setQuestionData] = useState(questionDefaultValues)

  const [answerData, setAnswerData] = useState<ILikerOptionModel[]>(
    likertAnswerDefaultValues
  )
  const [isFinished, setIsFinished] = useState(false)
  const [confirmationCallback, setConfirmationCallbackBase] = useState<
    (() => void) | null
  >(null)

  const setConfirmationCallback = (callback: () => void) => {
    if (confirmed.current) return callback()
    setConfirmationCallbackBase(() => () => callback())
  }

  useEffect(() => {
    dispatch({
      type: fromQuestionActions.GET_QUESTIONS_LIST,
      instanceId: instanceId,
    })
  }, [blockId, dispatch, instanceId])

  const { list } = useSelector(({ questions }: IMainState) => questions)
  const { edit } = useSelector(({ blocks }: IMainState) => blocks)

  useEffect(() => {
    setQuestions(list)
  }, [list])

  const handleSave = (
    data: IQuestionLikertModel<BlockTypes.BlockSurvey> | IOpenEndedModel,
    type: string
  ) => {
    const callback = () => {
      switch (type) {
        case 'Questions::Likert':
          dispatch({
            type: fromQuestionActions.QUESTION_LIKERT_CREATE,
            data: { data, instanceId: instanceId },
          })
          break
        case 'Questions::OpenEnded':
          dispatch({
            type: fromQuestionActions.QUESTION_OPENENDED_CREATE,
            data: { data, instanceId: instanceId },
          })
          break
        default:
          break
      }
      if (isFinished) {
        setIsFinished(false)
        onCancel()
      }
    }
    if (edit.hasResponses) return setConfirmationCallback(callback)
    callback()
  }

  const handleEdit = (id: number, data: any, type: string) => {
    const callback = () => {
      switch (type) {
        case 'Questions::Likert':
          dispatch({
            type: fromQuestionActions.QUESTION_LIKERT_EDIT,
            data: { data, instanceId: instanceId, questionId: id },
          })
          break
        case 'Questions::OpenEnded':
          dispatch({
            type: fromQuestionActions.QUESTION_OPENENDED_EDIT,
            data: { data, instanceId: instanceId, questionId: id },
          })
          break
        default:
          break
      }
      setOnEdit(0)
    }
    if (edit.hasResponses) return setConfirmationCallback(callback)
    callback()
  }

  const handleDelete = (id: number, type: string) => {
    const callback = () => {
      switch (type) {
        case 'Likert':
          dispatch({
            type: fromQuestionActions.QUESTION_LIKERT_DESTROY,
            payload: { questionId: id, instanceId },
          })
          break
        case 'Open Ended':
          dispatch({
            type: fromQuestionActions.QUESTION_OPENENDED_DESTROY,
            payload: { questionId: id, instanceId },
          })
          break
        default:
          break
      }
    }
    if (edit.hasResponses) return setConfirmationCallback(callback)
    callback()
  }

  const handleOnSave = () => {
    let data: any
    let index: number

    if (questionData['name'] && questionData.type.label !== '') {
      switch (questionData.type.value) {
        case 'Questions::Likert':
          if (onEdit) {
            index = questions.findIndex(
              (question) => question.questionableId === onEdit
            )
            index = index === -1 ? questions.length + 1 : questions[index].order
            data = prepareLikertQuestionData(
              questionData,
              answerData,
              index,
              blockId
            )
            handleEdit(onEdit, data, questionData.type.value)
          } else {
            index = questions.length + 1
            data = prepareLikertQuestionData(
              questionData,
              answerData,
              index,
              blockId
            )
            handleSave(data, questionData.type.value)
          }
          break
        case 'Questions::OpenEnded':
          if (onEdit) {
            // @ts-ignore
            index = questions.findIndex(
              (question) => question.questionableId === onEdit
            )
            // @ts-ignore
            index = index === -1 ? questions.length + 1 : questions[index].order
            data = prepareOpenEndedData(questionData, index, blockId)
            handleEdit(onEdit, data, questionData.type.value)
          } else {
            index = questions.length + 1
            data = prepareOpenEndedData(questionData, index, blockId)
            handleSave(data, questionData.type.value)
          }
          break
        default:
          break
      }
      setQuestionData(questionDefaultValues)
      setAnswerData(likertAnswerDefaultValues)
      setIsFinished(true)
    } else onCancel()
  }

  return (
    <>
      <div className={'d-flex flex-column'}>
        <h1 className={`mb-2 ${styles.title}`}>
          {t('administration.blocks.type.survey.question.plural')}
        </h1>
        {questions.length > 0 && (
          <>
            {questions.map((question, index: number) => {
              const { id, name, type, questionableId, mandatory, options } =
                question
              return onEdit === questionableId ? (
                <SurveyQuestionsEditor
                  key={id}
                  id={questionableId}
                  index={index + 1}
                  blockId={blockId}
                  data={question}
                  onSave={(data: any, type: string) =>
                    handleEdit(questionableId, data, type)
                  }
                  onDiscard={() => setOnEdit(0)}
                  sQuestionData={(sQuestionData) =>
                    setQuestionData(sQuestionData)
                  }
                  sAnswerData={(sAnswerData) => setAnswerData(sAnswerData)}
                />
              ) : (
                <SurveyQuestionCard
                  key={id}
                  id={questionableId}
                  index={index}
                  name={name}
                  mandatory={mandatory}
                  type={type}
                  options={options}
                  onEdit={() => setOnEdit(questionableId)}
                  onDelete={() => handleDelete(questionableId, type)}
                />
              )
            })}
          </>
        )}
        {!onEdit && (
          <SurveyQuestionsEditor
            key={questions.length + 1}
            index={questions.length + 1}
            blockId={blockId}
            onSave={(data: any, type: string) => handleSave(data, type)}
            sQuestionData={(sQuestionData: any) =>
              setQuestionData(sQuestionData)
            }
            sAnswerData={(sAnswerData: any) => setAnswerData(sAnswerData)}
          />
        )}
      </div>
      <ButtonToolbar className="float-right mt-3">
        <Button
          onClick={onCancel}
          variant={ButtonVariant.Secondary}
          size={ButtonSizes.Small}
        >
          {t('administration.blocks.type.survey.buttons.close')}
        </Button>
        <Button
          onClick={handleOnSave}
          variant={ButtonVariant.Primary}
          size={ButtonSizes.Small}
        >
          {t('administration.blocks.type.survey.buttons.finalize')}
        </Button>
      </ButtonToolbar>
      <Confirmation
        show={!!confirmationCallback}
        onAccept={() => {
          confirmationCallback?.()
          promiseResolve.current?.()
          setConfirmationCallbackBase(null)
          confirmed.current = true
        }}
        onClose={() => {
          setConfirmationCallbackBase(null)
          promiseResolve.current?.()
        }}
      />
    </>
  )
}

export default SurveyQuestions
